/**
 * QuotePdfDocument — Documento PDF (@react-pdf/renderer) de una cotización.
 *
 * Renderiza todos los datos de la cotización: cliente, fechas, descripción,
 * tabla de productos, conceptos y totales (subtotal, IVA, total).
 *
 * Se usa junto a <PdfEditor readOnly document={<QuotePdfDocument .../>} /> para
 * obtener vista previa + descarga sin permitir edición.
 */

import { Document, Page, Text, View, Image, StyleSheet } from "@react-pdf/renderer";
import dayjs from "dayjs";
import type { QuoteData } from "../types";
import type { ClientData } from "../../clients/types";
import type { Company } from "../../company/types";
import { statusQuote } from "../contants/statusQuote";
import { lineIva, lineQuantity, lineSubtotal, lineUnitPrice, resolveQuoteTotals } from "../utils/quoteTotals";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const money = (n: number | string | undefined) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(n) || 0);

const formatDate = (dateStr?: string) => (dateStr ? dayjs(dateStr).format("DD MMM YYYY") : "—");

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  page: {
    paddingTop: 48,
    paddingBottom: 56,
    paddingHorizontal: 48,
    fontFamily: "Helvetica",
    fontSize: 10,
    color: "#1f2937",
    lineHeight: 1.5,
  },
  // Header
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    borderBottom: "2 solid #1d4ed8",
    paddingBottom: 12,
    marginBottom: 18,
  },
  title: { fontSize: 22, fontFamily: "Helvetica-Bold", color: "#1d4ed8" },
  subtitle: { fontSize: 9, color: "#6b7280", marginTop: 2 },
  headerLeft: { flex: 1, paddingRight: 16, flexDirection: "row", alignItems: "flex-start", gap: 12 },
  logo: { width: 64, height: 64, objectFit: "contain" },
  emitter: { flex: 1 },
  company: { fontSize: 14, fontFamily: "Helvetica-Bold", color: "#111827" },
  headerRight: { alignItems: "flex-end" },
  folio: { fontSize: 12, fontFamily: "Helvetica-Bold" },
  statusBadge: {
    marginTop: 4,
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: "#1d4ed8",
    backgroundColor: "#dbeafe",
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: 4,
  },
  // Sections
  sectionTitle: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: 1,
    marginBottom: 6,
  },
  twoCol: { flexDirection: "row", gap: 24, marginBottom: 18 },
  col: { flex: 1 },
  label: { color: "#6b7280", fontSize: 9 },
  value: { fontFamily: "Helvetica-Bold", fontSize: 10, marginBottom: 4 },
  infoRow: { flexDirection: "row", justifyContent: "space-between", marginBottom: 3 },
  paragraph: { fontSize: 10, color: "#374151" },
  block: { marginBottom: 18 },
  // Table
  table: { marginBottom: 18 },
  tHead: {
    flexDirection: "row",
    backgroundColor: "#1d4ed8",
    color: "#ffffff",
    paddingVertical: 6,
    paddingHorizontal: 6,
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    textTransform: "uppercase",
  },
  tRow: {
    flexDirection: "row",
    paddingVertical: 6,
    paddingHorizontal: 6,
    borderBottom: "1 solid #e5e7eb",
    fontSize: 9,
  },
  tRowAlt: { backgroundColor: "#f9fafb" },
  cDesc: { flex: 4 },
  cQty: { flex: 1, textAlign: "center" },
  cPrice: { flex: 1.6, textAlign: "right" },
  cIva: { flex: 1.3, textAlign: "right" },
  cTotal: { flex: 1.6, textAlign: "right" },
  // Totals
  totals: { flexDirection: "row", justifyContent: "flex-end" },
  totalsBox: { width: 220 },
  totalsRow: { flexDirection: "row", justifyContent: "space-between", paddingVertical: 3 },
  totalsLabel: { color: "#6b7280" },
  grandRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 6,
    marginTop: 4,
    borderTop: "2 solid #1d4ed8",
  },
  grandLabel: { fontFamily: "Helvetica-Bold", fontSize: 12, color: "#1d4ed8" },
  grandValue: { fontFamily: "Helvetica-Bold", fontSize: 12, color: "#1d4ed8" },
  // Signature
  signatureBlock: { marginTop: 36, alignItems: "flex-start" },
  signatureImg: { width: 160, height: 60, objectFit: "contain" },
  signatureLine: { width: 200, borderTop: "1 solid #1f2937", marginTop: 4, paddingTop: 4 },
  signatureName: { fontSize: 9, fontFamily: "Helvetica-Bold", color: "#1f2937" },
  signatureLabel: { fontSize: 7, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.5 },
  // Footer
  footer: {
    position: "absolute",
    bottom: 28,
    left: 48,
    right: 48,
    textAlign: "center",
    fontSize: 8,
    color: "#9ca3af",
    borderTop: "1 solid #e5e7eb",
    paddingTop: 8,
  },
});

// ─── Props ────────────────────────────────────────────────────────────────────

interface QuotePdfDocumentProps {
  quote: QuoteData;
  client?: ClientData;
  company?: Company;
  /**
   * Logotipo de la empresa como data URI (`data:image/png;base64,...`).
   * Se obtiene con `useCompanyLogoImage`; si no hay logo, el encabezado
   * simplemente se renderiza sin él.
   */
  logoSrc?: string;
  /**
   * Firma dibujada para la cotización, como data URI. Se obtiene con
   * `useQuoteSignatureImage`; si la cotización no está firmada, no se
   * renderiza el bloque de firma.
   */
  signatureSrc?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function QuotePdfDocument({ quote, client, company, logoSrc, signatureSrc }: QuotePdfDocumentProps) {
  const products = quote.products ?? [];

  // Los importes se calculan a partir de las líneas: es la única fuente de verdad.
  const { subtotal, iva, total } = resolveQuoteTotals(quote);

  const statusLabel = statusQuote[quote.status_quote]?.label ?? quote.status_quote;

  const clientFullName = client
    ? [client.client_name, client.client_lastname_1, client.client_lastname_2].filter(Boolean).join(" ")
    : undefined;

  const companyName = company?.public_name_company?.trim() || company?.name_company?.trim();

  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        {/* ── Header ── */}
        <View style={styles.headerRow}>
          {/* Emisor (empresa que cotiza) */}
          <View style={styles.headerLeft}>
            {logoSrc ? <Image src={logoSrc} style={styles.logo} /> : null}
            <View style={styles.emitter}>
              <Text style={styles.company}>{companyName || `Empresa #${quote.id_company}`}</Text>
              {company?.public_name_company?.trim() && company?.name_company?.trim()
                && company.public_name_company.trim() !== company.name_company.trim() ? (
                <Text style={styles.subtitle}>{company.name_company}</Text>
              ) : null}
              {company?.rfc_company ? <Text style={styles.subtitle}>RFC: {company.rfc_company}</Text> : null}
              {company?.capital_regime ? (
                <Text style={styles.subtitle}>Régimen de capital: {company.capital_regime}</Text>
              ) : null}
            </View>
          </View>

          {/* Tipo de documento + folio */}
          <View style={styles.headerRight}>
            <Text style={styles.title}>COTIZACIÓN</Text>
          </View>
        </View>

        {/* ── Cliente + Fechas ── */}
        <View style={styles.twoCol}>
          <View style={styles.col}>
            <Text style={styles.sectionTitle}>Cliente</Text>
            {client ? (
              <>
                <Text style={styles.value}>
                  {client.client_company_name?.trim() || clientFullName || "—"}
                </Text>
                {clientFullName && client.client_company_name?.trim() ? (
                  <Text style={styles.label}>{clientFullName}</Text>
                ) : null}
                {client.client_rfc ? <Text style={styles.label}>RFC: {client.client_rfc}</Text> : null}
                {client.client_email ? <Text style={styles.label}>{client.client_email}</Text> : null}
                {client.client_phone ? <Text style={styles.label}>Tel: {client.client_phone}</Text> : null}
              </>
            ) : (
              <Text style={styles.label}>Cliente #{quote.id_client}</Text>
            )}
          </View>

          <View style={styles.col}>
            <Text style={styles.sectionTitle}>Detalles</Text>
            <View style={styles.infoRow}>
              <Text style={styles.label}>Fecha de cotización</Text>
              <Text style={styles.value}>{formatDate(quote.date_quote)}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.label}>Fecha de expiración</Text>
              <Text style={styles.value}>{formatDate(quote.date_expiration)}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.label}>Estatus</Text>
              <Text style={styles.value}>{statusLabel}</Text>
            </View>
          </View>
        </View>

        {/* ── Texto de presentación ── */}
        {quote.quote_wave ? (
          <View style={styles.block}>
            <Text style={styles.sectionTitle}>Presentación</Text>
            <Text style={styles.paragraph}>{quote.quote_wave}</Text>
          </View>
        ) : null}

        {/* ── Descripción ── */}
        {quote.description_quote ? (
          <View style={styles.block}>
            <Text style={styles.sectionTitle}>Descripción</Text>
            <Text style={styles.paragraph}>{quote.description_quote}</Text>
          </View>
        ) : null}

        {/* ── Tabla de productos ── */}
        <View style={styles.table}>
          <Text style={styles.sectionTitle}>Productos y servicios</Text>
          <View style={styles.tHead}>
            <Text style={styles.cDesc}>Descripción</Text>
            <Text style={styles.cQty}>Cant.</Text>
            <Text style={styles.cPrice}>P. Unitario</Text>
            <Text style={styles.cIva}>IVA</Text>
            <Text style={styles.cTotal}>Importe</Text>
          </View>
          {products.length > 0 ? (
            products.map((p, i) => (
              <View
                key={p.id_quote_product ?? p.id_product ?? i}
                style={[styles.tRow, ...(i % 2 ? [styles.tRowAlt] : [])]}
              >
                <Text style={styles.cDesc}>
                  {p.product_name || p.product_description || "—"}
                  {p.product_description && p.product_name && p.product_description !== p.product_name
                    ? `\n${p.product_description}`
                    : ""}
                </Text>
                <Text style={styles.cQty}>{lineQuantity(p)}</Text>
                <Text style={styles.cPrice}>{money(lineUnitPrice(p))}</Text>
                <Text style={styles.cIva}>{money(lineIva(p))}</Text>
                <Text style={styles.cTotal}>{money(lineSubtotal(p))}</Text>
              </View>
            ))
          ) : (
            <View style={styles.tRow}>
              <Text style={styles.cDesc}>Sin productos registrados.</Text>
            </View>
          )}
        </View>

        {/* ── Conceptos ── */}
        {quote.concepts && quote.concepts.length > 0 ? (
          <View style={styles.block}>
            <Text style={styles.sectionTitle}>Conceptos</Text>
            {quote.concepts.map((c) => (
              <Text key={c.id_concept} style={styles.paragraph}>
                • {c.concept_name}
              </Text>
            ))}
          </View>
        ) : null}

        {/* ── Totales ── */}
        <View style={styles.totals}>
          <View style={styles.totalsBox}>
            <View style={styles.totalsRow}>
              <Text style={styles.totalsLabel}>Subtotal</Text>
              <Text>{money(subtotal)}</Text>
            </View>
            <View style={styles.totalsRow}>
              <Text style={styles.totalsLabel}>IVA</Text>
              <Text>{money(iva)}</Text>
            </View>
            <View style={styles.grandRow}>
              <Text style={styles.grandLabel}>Total</Text>
              <Text style={styles.grandValue}>{money(total)}</Text>
            </View>
          </View>
        </View>

        {/* ── Firma ── */}
        {quote.uuid_signature_quote && signatureSrc ? (
          <View style={styles.signatureBlock}>
            <Image src={signatureSrc} style={styles.signatureImg} />
            <View style={styles.signatureLine}>
              <Text style={styles.signatureName}>{quote.signature_name || "—"}</Text>
              <Text style={styles.signatureLabel}>Firma</Text>
            </View>
          </View>
        ) : null}

        {/* ── Footer ── */}
        <Text
          style={styles.footer}
          render={({ pageNumber, totalPages }) =>
            `Cotización #${quote.id_quote} · Página ${pageNumber} de ${totalPages}`
          }
          fixed
        />
      </Page>
    </Document>
  );
}
